// Live updates for messaging: `thread.updated` and `message.updated` on the existing SSE channel.
// Events carry hints, not state: each one schedules a coalesced refetch of the affected thread.
//
// Seam: the workspace's single event stream (state/workspace.tsx) doesn't expose its events to
// features yet. Until it calls `messagingEvents.publish(event)` (and `messagingEvents.attach()` once),
// the messaging page opens its own stream while it's mounted, publishing into the same hub.
import type { EventEnvelope } from '@bcio-voice/contracts';
import { EventStream, type StreamStatus } from '../../lib/events.ts';
import { isRecord } from './model.ts';

export type MessagingEventDecision =
  | { kind: 'thread'; threadId: string }
  | { kind: 'message'; threadId: string | null; messageId: string };

const THREAD_TYPES: ReadonlySet<string> = new Set(['thread.updated', 'messaging.thread.updated']);
const MESSAGE_TYPES: ReadonlySet<string> = new Set(['message.updated', 'messaging.message.updated']);
const THREAD_AGGREGATES: ReadonlySet<string> = new Set(['message_thread', 'messaging_thread']);
const MESSAGE_AGGREGATES: ReadonlySet<string> = new Set(['message', 'messaging_message']);
const ID = /^[A-Za-z0-9_.:-]{1,128}$/;

/** Which messaging resource an event points at; null for every other event. */
export function messagingEventDecision(event: Pick<EventEnvelope, 'type' | 'aggregate' | 'data'>): MessagingEventDecision | null {
  const aggregateId = event.aggregate.id;
  if (THREAD_TYPES.has(event.type) || THREAD_AGGREGATES.has(event.aggregate.type)) return { kind: 'thread', threadId: aggregateId };
  if (MESSAGE_TYPES.has(event.type) || MESSAGE_AGGREGATES.has(event.aggregate.type)) {
    const data = isRecord(event.data) ? event.data : {};
    const threadId = typeof data.thread_id === 'string' && ID.test(data.thread_id) ? data.thread_id : null;
    return { kind: 'message', threadId, messageId: aggregateId };
  }
  return null;
}

type Listener = (event: EventEnvelope) => void;

/** Fan-out point for messaging events, fed by the workspace stream or the page's own stream. */
export class MessagingEventHub {
  private readonly listeners = new Set<Listener>();
  private readonly resyncListeners = new Set<() => void>();
  private externalSource = false;

  /** The workspace stream feeds this hub; pages then don't open a stream of their own. */
  attach(): void {
    this.externalSource = true;
  }

  get external(): boolean {
    return this.externalSource;
  }

  publish(event: EventEnvelope): void {
    if (!messagingEventDecision(event)) return;
    for (const listener of [...this.listeners]) {
      try {
        listener(event);
      } catch {
        // A failing view never stops the others.
      }
    }
  }

  /** The stream lost its place (replay window expired): views reload what they show. */
  resync(): void {
    for (const listener of [...this.resyncListeners]) {
      try {
        listener();
      } catch {
        // Same isolation as publish.
      }
    }
  }

  subscribe(onEvent: Listener, onResync: () => void): () => void {
    this.listeners.add(onEvent);
    this.resyncListeners.add(onResync);
    return () => {
      this.listeners.delete(onEvent);
      this.resyncListeners.delete(onResync);
    };
  }
}

export const messagingEvents = new MessagingEventHub();

/**
 * Starts a page-scoped stream into `hub` unless the workspace already feeds it. Returns the stop
 * function. Session expiry and access changes are handled by the workspace's own stream and the API
 * client; this stream only stops.
 */
export function startPageStream(options: {
  hub: MessagingEventHub;
  url: string;
  fetch?: typeof fetch;
  onStatus?: (status: StreamStatus) => void;
}): () => void {
  if (options.hub.external) return () => undefined;
  const stream = new EventStream({
    url: options.url,
    ...(options.fetch ? { fetch: options.fetch } : {}),
    onEvent: event => options.hub.publish(event),
    onResync: () => options.hub.resync(),
    onSessionExpired: () => undefined,
    ...(options.onStatus ? { onStatus: options.onStatus } : {}),
  });
  stream.start(null);
  return () => stream.stop();
}
